import { useCallback, useEffect, useRef } from "react";
import type { RxCollection } from "rxdb";
import type { EpocusExam } from "../../../../global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamWorksheetData } from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";

const DEBOUNCE_MS = 300;

export const useWorksheetAutoSave = (
  collection: RxCollection<EpocusExam> | null,
  examId: string | null,
  worksheetData: ExamWorksheetData | null,
  disabled = false,
): { cancel: () => void } => {
  const latestRef = useRef(worksheetData);
  // null means "baseline not yet established". The first non-null worksheetData
  // sets the baseline without triggering a save — it is a load event, not a
  // user edit. Using null rather than JSON.stringify(null) avoids scheduling a
  // spurious save when worksheetData transitions from null (exam loading) to the
  // first real value.
  const prevJsonRef = useRef<string | null>(
    worksheetData ? JSON.stringify(worksheetData) : null,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disabledRef = useRef(disabled);
  const cancelledRef = useRef(false);

  latestRef.current = worksheetData;
  disabledRef.current = disabled;

  // Establish the baseline synchronously on the first render where worksheetData
  // is non-null. The debounce effect also does this, but it runs after paint —
  // meaning an edit made in that window would cancel the effect, set prevJsonRef
  // to the edited value, and the first edit would never be persisted (including
  // via flush on unmount).
  if (prevJsonRef.current === null && worksheetData !== null) {
    prevJsonRef.current = JSON.stringify(worksheetData);
  }

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    clearTimer();
    cancelledRef.current = true;
  }, [clearTimer]);

  const flush = useCallback(() => {
    clearTimer();

    if (disabledRef.current || cancelledRef.current) return;
    if (!collection || !examId || !latestRef.current) return;

    const nextJson = JSON.stringify(latestRef.current);
    // No baseline yet — sync without saving (exam was still loading on mount).
    if (prevJsonRef.current === null) {
      prevJsonRef.current = nextJson;
      return;
    }
    if (nextJson === prevJsonRef.current) return;

    prevJsonRef.current = nextJson;
    void collection
      .findOne(examId)
      .exec()
      .then(
        (doc) =>
          latestRef.current &&
          doc?.incrementalPatch({ worksheetData: latestRef.current }),
      )
      .catch((err) => console.error("Auto-save failed:", err));
  }, [collection, examId, clearTimer]);

  useEffect(() => {
    if (disabled || !worksheetData) return;

    const nextJson = JSON.stringify(worksheetData);
    // No baseline yet — establish it without scheduling a save.
    if (prevJsonRef.current === null) {
      prevJsonRef.current = nextJson;
      return;
    }
    if (nextJson === prevJsonRef.current) return;

    cancel();
    cancelledRef.current = false;
    timerRef.current = setTimeout(flush, DEBOUNCE_MS);
  }, [worksheetData, flush, disabled, cancel]);

  useEffect(
    () => () => {
      flush();
    },
    [flush],
  );

  return { cancel };
};
